"use client";

import Image from "next/image";
import { ModernButton } from "@/components/ui/ModernButton";
import GlassButton from "@/components/ui/GlassButton";
import { Tag, ArrowUpRight, ChevronRight } from "lucide-react";
import { MiniNavBar } from "@/components/public/MiniNavBar";
import { usePathname } from "next/navigation";
import { getPageColorsFromRoute } from "@/lib/navigation";
import { useEffect, useState } from "react";
import Section from "@/components/public/Section";
import { getHeroImageSrc, HERO_IMAGE_DIMENSIONS } from "@/lib/heroImages";
import { processAuthUrl } from "@/lib/appStage";

interface FeatureCard {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface PageHeroProps {
  tagline?: {
    text: string;
    href?: string;
    icon?: string;
  };
  title: string;
  description: string;
  primaryButton?: {
    text: string;
    href: string;
    variant?: string;
  };
  secondaryButton?: {
    text: string;
    href: string;
    variant?: string;
  };
  image?: {
    src: string;
    alt: string;
    mediaUri?: string; // Future-proofing for media URIs
    width?: number;
    height?: number;
    // optional: percentage across the top where the slanted crop should meet (0-100)
    slantTop?: number;
    // optional: preferred focal point for the image when using object-cover
    focus?: "left" | "center" | "right";
  };
  reverse?: boolean; // For switching left/right layout
  featureCards?: FeatureCard[];
}

export function PageHero({
  tagline,
  title,
  description,
  primaryButton,
  secondaryButton,
  image,
  reverse = false,
  featureCards,
}: PageHeroProps) {
  const pathname = usePathname();
  const pageColors = getPageColorsFromRoute(pathname || "");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(typeof window !== "undefined" && window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return (
    <div className="relative z-10 w-full overflow-hidden sm:pl-0 md:pl-6 xl:pl-6">
      {/* MiniNavBar - consistent responsive padding matching Section and LandingTopBar */}
      <div className="px-4 sm:px-6 xl:px-0 xl:mx-[16.67%] sm:mb-6 md:mb-4">
        <MiniNavBar />
      </div>
      
      {/* Hero content - responsive grid layout */}
      <div className="sm:mt-3 md:mt-8 xl:mt-12 w-full">
        {/* Mobile: stacked | Desktop: 6-column grid with content in cols 2-3, image in cols 4-6 */}
        <div className="xl:grid xl:grid-cols-6 xl:items-center w-full">
          
          {/* Content Section - consistent padding: px-4 mobile, px-6 tablet, grid-aligned desktop */}
          <div className={`px-4 sm:px-6 xl:px-0 xl:col-start-2 xl:col-span-2 ${reverse ? "xl:order-2" : "xl:order-1"}`}>
            <div className="xl:pr-8 2xl:pr-12">
              {/* Tagline */}
              {tagline && (
                <div className="mb-6">
                  {tagline.href ? (
                    <div className="inline-flex items-center gap-3 text-[color:var(--homepage-primary)/80] hover:text-[color:var(--homepage-primary)]/80 transition-all duration-200 group cursor-pointer">
                      {tagline.icon ? (
                        <Image
                          src={tagline.icon}
                          alt=""
                          width={24}
                          height={24}
                          className="w-6 h-6 object-contain"
                        />
                      ) : (
                        <Tag className="w-5 h-5" />
                      )}
                      <span className="text-sm font-medium">
                        {tagline.text}
                      </span>
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 text-blue-700 rounded-full">
                      {tagline.icon ? (
                        <Image
                          src={tagline.icon}
                          alt=""
                          width={20}
                          height={20}
                          className="w-5 h-5 object-contain"
                        />
                      ) : (
                        <Tag className="w-4 h-4" />
                      )}
                      <span className="text-sm font-semibold uppercase tracking-wide">{tagline.text}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl xl:text-4xl 2xl:text-5xl font-black text-gray-900 leading-tight mb-4 sm:mb-6">
                {title}
              </h1>

              {/* Description */}
              <p className="text-sm sm:text-base xl:text-lg leading-relaxed text-gray-600 font-light mb-6 sm:mb-8 max-w-2xl">
                {description}
              </p>

              {/* Buttons */}
              {primaryButton && (
                <div className="flex flex-row flex-wrap gap-3 sm:gap-4">
                  <GlassButton
                    href={processAuthUrl(primaryButton.href)}
                    size="lg"
                    ariaLabel={primaryButton.text}
                    className="flex-shrink-0"
                    style={{
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02)), var(--pagehero-primary-bg, var(--mininav-active-color))',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.12)',
                      boxShadow: '0 10px 30px -12px rgba(2,6,23,0.18)',
                    }}
                  >
                    <span className="text-sm sm:text-base">{primaryButton.text}</span>
                    <ChevronRight className="w-4 h-4 text-white" />
                  </GlassButton>
                </div>
              )}
            </div>
          </div>

          {/* Image Section - spans columns 4-6 (extends to right edge) */}
          {image && (
            <div className={`xl:col-start-4 sm:pl-0 md:pl-0 xl:pl-6 xl:col-span-3 ${reverse ? "xl:order-1" : "xl:order-2"} mt-8 sm:mt-10 xl:mt-0`}>
              {/* Mobile: contained with padding | Desktop: extends to right edge */}
              <div className="px-4 sm:px-6 xl:px-0 xl:ml-auto xl:w-[90%]">
                <div
                  className="relative w-full overflow-hidden xl:rounded-l-2xl p-4 sm:p-5 md:p-6 lg:p-7 xl:p-8 max-h-[500px] md:max-h-[600px] xl:max-h-none"
                  style={Object.assign({
                    // Exact aspect ratio for 1780x1540 images - no min/max height constraints
                    aspectRatio: HERO_IMAGE_DIMENSIONS.aspectRatio
                  },
                    image.slantTop
                      ? (() => {
                          const bottomLeft = isMobile ? 90 : 60;
                          const clip = `polygon(${image.slantTop}% 0, 100% 0, 100% 100%, 0% ${bottomLeft}%)`;
                          return { clipPath: clip, WebkitClipPath: clip };
                        })()
                      : {}
                  )}
                >
                  <Image
                    src={getHeroImageSrc(image)}
                    alt={image.alt}
                    fill
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    className="object-contain object-center"
                    priority
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

